import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Table, Button, Input, Select, Space, Typography, Spin, Tabs, TabPane,
  Tag, InputNumber, Toast
} from "@douyinfe/semi-ui";
import { ApiClient } from "../../api/ApiClient";

const { Text } = Typography;

interface LogFile {
  name: string;
  size: number;
  lastModified: number;
  gz: boolean;
}

interface LogLine {
  lineNum: number;
  content: string;
  timestamp: string | null;
  level: string | null;
  matched: boolean;
}

interface SearchResult {
  total: number;
  lines: LogLine[];
}

const CONTEXT_OPTIONS = [
  { value: "NONE", label: "无上下文" },
  { value: "BEFORE", label: "前N行" },
  { value: "AFTER", label: "后N行" },
  { value: "BOTH", label: "前后N行" },
];

const LEVEL_OPTIONS = [
  { value: "", label: "全部" },
  { value: "ERROR", label: "ERROR" },
  { value: "WARN", label: "WARN" },
  { value: "INFO", label: "INFO" },
  { value: "DEBUG", label: "DEBUG" },
];

const LEVEL_COLORS: Record<string, string> = {
  ERROR: "red",
  WARN: "orange",
  INFO: "blue",
  DEBUG: "grey",
  TRACE: "grey",
};

export default function LogViewer() {
  const [files, setFiles] = useState<LogFile[]>([]);
  const [loading, setLoading] = useState(false);

  // Search state
  const [keyword, setKeyword] = useState("");
  const [level, setLevel] = useState("");
  const [selectedFile, setSelectedFile] = useState("spring.log");
  const [contextMode, setContextMode] = useState("NONE");
  const [contextLines, setContextLines] = useState(5);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [searchResult, setSearchResult] = useState<SearchResult | null>(null);

  // Live state
  const [liveFile, setLiveFile] = useState("spring.log");
  const [liveConnected, setLiveConnected] = useState(false);
  const [liveLines, setLiveLines] = useState<string[]>([]);
  const liveRef = useRef<HTMLDivElement>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const autoScrollRef = useRef(true);

  const api = ApiClient.getDefault();

  const loadFiles = async () => {
    const resp = await api.request("/logs/files", "GET");
    if (resp.code === "ok") {
      setFiles((resp.data as LogFile[]) || []);
    }
  };

  useEffect(() => {
    loadFiles();
  }, []);

  const doSearch = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (keyword) params.set("keyword", keyword);
      if (level) params.set("level", level);
      params.set("file", selectedFile);
      params.set("contextMode", contextMode);
      params.set("contextLines", String(contextLines));
      params.set("page", String(page));
      params.set("size", String(pageSize));
      const resp = await api.request("/logs/search?" + params.toString(), "GET");
      if (resp.code === "ok") {
        setSearchResult(resp.data as SearchResult);
      }
    } finally {
      setLoading(false);
    }
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / 1024 / 1024).toFixed(1) + " MB";
  };

  // Live log
  const connectLive = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }
    const baseUrl = (api as any).baseUrl || "";
    const token = (api as any).token || localStorage.getItem("token") || "";
    const url = `${baseUrl}/logs/stream?file=${encodeURIComponent(liveFile)}&X-Auth-Token=${token}`;
    const es = new EventSource(url);
    eventSourceRef.current = es;

    es.onmessage = (e) => {
      setLiveLines((prev) => {
        const next = [...prev, e.data as string];
        if (next.length > 2000) return next.slice(-1500);
        return next;
      });
    };

    es.onerror = () => {
      setLiveConnected(false);
      es.close();
      eventSourceRef.current = null;
    };

    setLiveConnected(true);
    setLiveLines([]);
  }, [liveFile, api]);

  const disconnectLive = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setLiveConnected(false);
  };

  useEffect(() => {
    if (autoScrollRef.current && liveRef.current) {
      liveRef.current.scrollTop = liveRef.current.scrollHeight;
    }
  }, [liveLines]);

  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  const columns = [
    {
      title: "行号",
      dataIndex: "lineNum",
      width: 70,
    },
    {
      title: "级别",
      dataIndex: "level",
      width: 70,
      render: (val: string | null) => val ? <Tag color={LEVEL_COLORS[val] as any || "grey"} size="small">{val}</Tag> : "-",
    },
    {
      title: "内容",
      dataIndex: "content",
      render: (val: string, record: LogLine) => (
        <pre style={{
          margin: 0,
          whiteSpace: "pre-wrap",
          wordBreak: "break-all",
          fontFamily: "Consolas, Monaco, monospace",
          fontSize: 12,
          background: record.matched ? "#fffbe6" : "transparent",
          padding: "2px 4px",
        }}>{val}</pre>
      ),
    },
  ];

  const fileOptions = files.map((f) => ({
    value: f.name,
    label: `${f.name} (${formatSize(f.size)})`,
  }));

  if (fileOptions.length === 0) {
    fileOptions.push({ value: "spring.log", label: "spring.log" });
  }

  return (
    <div style={{ padding: 16 }}>
      <Tabs type="line">
        <TabPane tab="日志搜索" itemKey="search">
          <Space style={{ marginBottom: 12, flexWrap: "wrap" }}>
            <Select value={selectedFile} onChange={setSelectedFile} style={{ width: 240 }} optionList={fileOptions} />
            <Input placeholder="关键词" value={keyword} onChange={setKeyword} style={{ width: 200 }} />
            <Select value={level} onChange={setLevel} style={{ width: 120 }} optionList={LEVEL_OPTIONS} />
            <Select value={contextMode} onChange={setContextMode} style={{ width: 120 }} optionList={CONTEXT_OPTIONS} />
            {contextMode !== "NONE" && (
              <InputNumber min={1} max={50} value={contextLines} onChange={(v) => setContextLines(Number(v) || 5)} style={{ width: 80 }} />
            )}
            <Button theme="solid" onClick={doSearch} loading={loading}>搜索</Button>
          </Space>
          {searchResult && (
            <>
              <Text type="tertiary" style={{ marginBottom: 8, display: "block" }}>
                共 {searchResult.total} 条结果
              </Text>
              <Spin spinning={loading}>
                <Table
                  columns={columns}
                  dataSource={searchResult.lines}
                  rowKey="lineNum"
                  size="small"
                  pagination={{
                    currentPage: page,
                    pageSize,
                    total: searchResult.total,
                    onPageChange: (p) => { setPage(p); doSearch(); },
                  }}
                />
              </Spin>
            </>
          )}
        </TabPane>

        <TabPane tab="实时日志" itemKey="live">
          <Space style={{ marginBottom: 12 }}>
            <Select value={liveFile} onChange={setLiveFile} style={{ width: 240 }} optionList={fileOptions} />
            {!liveConnected ? (
              <Button theme="solid" onClick={connectLive}>连接</Button>
            ) : (
              <Button type="danger" onClick={disconnectLive}>断开</Button>
            )}
            <Button onClick={() => setLiveLines([])}>清屏</Button>
            <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
              <input type="checkbox" checked={autoScrollRef.current} onChange={(e) => { autoScrollRef.current = e.target.checked; }} />
              自动滚动
            </label>
          </Space>
          <div
            ref={liveRef}
            style={{
              background: "#1e1e1e",
              color: "#d4d4d4",
              padding: 12,
              borderRadius: 6,
              height: "calc(100vh - 260px)",
              overflowY: "auto",
              fontFamily: "Consolas, Monaco, monospace",
              fontSize: 12,
              lineHeight: 1.6,
            }}
          >
            {liveLines.length === 0 && !liveConnected && (
              <Text style={{ color: "#888" }}>点击"连接"开始查看实时日志</Text>
            )}
            {liveLines.map((line, i) => {
              let color = "#d4d4d4";
              if (line.includes(" ERROR ")) color = "#f44747";
              else if (line.includes(" WARN ")) color = "#e5c07b";
              else if (line.includes(" INFO ")) color = "#61afef";
              else if (line.includes(" DEBUG ")) color = "#888";
              return <div key={i} style={{ color }}>{line}</div>;
            })}
          </div>
        </TabPane>
      </Tabs>
    </div>
  );
}
